import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import FadeInView from "./FadeInView";

const FAQ_ITEMS = [
  {
    q: "How is LawFusion different from ChatGPT and other AI tools?",
    a: "ChatGPT gives you one response. LawFusion runs your issue through multiple AI models, compares their reasoning, highlights disagreement, and can run adversarial debate — so you see weaknesses before opposing counsel does.",
  },
  {
    q: "How do I know I can trust the results?",
    a: "LawFusion shows where models agree, where they diverge, and what facts, authority, or risks may still be missing. This supports better issue-spotting and more informed attorney judgment—not blind reliance on AI.\n\nThe models surface gaps and competing arguments; the attorney remains in control of the legal strategy and final decisions.",
  },
  {
    q: "Can I upload confidential client information?",
    a: "LawFusion is designed with privacy protections, but attorneys should still use professional judgment and avoid uploading materials they are not permitted to share. For sensitive matters, remove unnecessary identifying details when possible.",
  },
];

export default function NewLandingFaq() {
  const [openIndex, setOpenIndex] = useState(0);

  const toggle = (index) => {
    setOpenIndex((prev) => (prev === index ? -1 : index));
  };

  return (
    <section className="faq-section">
      <div className="faq-inner">
        <FadeInView>
          <p className="section-eyebrow">FAQ</p>
          <h2
            className="section-headline"
            style={{ fontSize: "clamp(22px, 3vw, 34px)" }}
          >
            Questions Attorneys Ask Before Trying LawFusion.
          </h2>
          <p
            className="section-sub"
            style={{
              textAlign: "center",
              fontSize: 15,
              color: "var(--muted)",
              lineHeight: 1.7,
              maxWidth: 520,
              margin: "0 auto 48px",
            }}
          >
            Short answers to the most common concerns before you test your first
            legal issue.
          </p>
        </FadeInView>
        <div className="faq-list">
          {FAQ_ITEMS.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div
                key={item.q}
                className={`faq-item${isOpen ? " open" : ""}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
              >
                <button
                  type="button"
                  className="faq-question nl-faq-trigger"
                  onClick={() => toggle(index)}
                  aria-expanded={isOpen}
                >
                  <span className="faq-question-text">{item.q}</span>
                  <span className={`faq-chevron${isOpen ? " is-open" : ""}`} aria-hidden="true">
                    <ChevronDown size={16} />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      className="faq-answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <div className="faq-answer-inner">{item.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
        <div className="faq-footer">
          <Link to="/faq" className="faq-all-link">
            See all FAQs
          </Link>
        </div>
      </div>
    </section>
  );
}
